import React, { useCallback, useState } from "react";
import { Grid, Typography } from "@mui/material";
import { useIntl } from "react-intl";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import { style } from "./style";

import Dialog from "@/components/Dialog";
import GetImages from "@/components/GetImages";
import Modal from "@/components/Modal";
import { InputField, TextArea } from "@/components/InputField";
import Button from "@/components/Button";

import { IButtonVariant } from "@/models/button";

import { createHelpSupportTicketApi } from "@/utils/apis/doctor/profile";
import { INotifyEnum, notify } from "@/utils/toaster";
import { uploadFileToS3Api } from "@/utils/apis/doctor/prescription";

import { IDocumentTypeEnum } from "@/models/doctor";
import {
  IModalKeyEnums,
  ISupportTicketPermissionEnums,
  IUploadDocumentEnums,
} from "@/models/permission";

import { getFileNameFromUrl, pdfRegex, truncateFileName } from "@/utils";
import useIsMobile from "@/utils/hooks/useIsMobile";
import useIsTablet from "@/utils/hooks/useIsTablet";
import { usePermissionRestriction } from "@/utils/hooks/usePermissionRestriction";

interface IProps {
  isOpen: boolean;
  closeHandler: () => void;
}
const HelpSupport: React.FC<IProps> = (props) => {
  const { isOpen, closeHandler } = props;

  const [fields, setFields] = useState({
    problem: "",
    description: "",
  });
  const [attachments, setAttachments] = useState<string[]>([]);
  const [fileAttachments, setFileAttachments] = useState<
    Array<{ fileName: string; isPdf: boolean }>
  >([]);
  const [isFileUploading, setIsFileUploading] = useState<boolean>(false);

  const intl = useIntl();
  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();
  const permissionRestrictionHandler = usePermissionRestriction();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFields((prevFields) => ({
      ...prevFields,
      [name]: value,
    }));
  };

  const uploadFileToS3 = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const isPermissionGranted = permissionRestrictionHandler(
      IModalKeyEnums.UPLOAD_DOCUMENT,
      IUploadDocumentEnums.UPLOAD_TO_S3
    );
    if (!isPermissionGranted) return;

    try {
      setIsFileUploading(true);
      const file = event.target.files?.[0];
      const formData = new FormData();
      formData.append("file", file!);
      const uploadFileToS3Response = await uploadFileToS3Api(
        IDocumentTypeEnum.SUPPORT_ATTACHMENT,
        formData
      );

      if ("result" in uploadFileToS3Response) {
        const { thumbnailUrl, url } = uploadFileToS3Response?.result;
        const fileName = truncateFileName(getFileNameFromUrl(url) || "");
        const isPdf = pdfRegex.test(url);
        setFileAttachments([{ fileName, isPdf }!, ...fileAttachments]);
        setAttachments([thumbnailUrl ?? url, ...attachments]);
      } else {
        notify(INotifyEnum.ERROR, uploadFileToS3Response?.message);
      }
    } finally {
      setIsFileUploading(false);
    }
  };

  const removeAttachmentsBeforeSubmitTicket = (indexVal: number) => {
    setFileAttachments(
      fileAttachments.filter((_, index) => index !== indexVal)
    );
    setAttachments(attachments.filter((_, index) => index !== indexVal));
  };
  const pdfImageUploadHandler = (type: string) => {
    document.getElementById(type)?.click();
  };
  const handleRequestCallbackSubmit = async () => {
    try {
      const isPermissionGranted = permissionRestrictionHandler(
        IModalKeyEnums.SUPPORT_TICKET,
        ISupportTicketPermissionEnums.CREATE_SUPPORT_TICKET
      );
      if (!isPermissionGranted) return;

      const helpSupportTicketResponse = await createHelpSupportTicketApi({
        problem: fields.problem,
        description: fields.description,
        attachments,
      });
      if ("result" in helpSupportTicketResponse) {
        closeHandler();
        notify(INotifyEnum.SUCCESS, helpSupportTicketResponse.message);
      } else {
        notify(INotifyEnum.ERROR, helpSupportTicketResponse?.message);
      }
    } catch (error) {
      console.log(error);
    }
  };
  const helpSupportScreen = useCallback(
    () => (
      <Grid item sx={style.container}>
        <Typography component={"h4"} sx={style.fieldSpacing}>
          {intl.formatMessage({
            id: "help_support",
            defaultMessage: "Help & Support",
          })}
        </Typography>
        <Grid item sx={style.fieldSpacing}>
          <InputField
            name="problem"
            value={fields.problem}
            onChange={handleChange}
            placeholder={intl.formatMessage({
              id: "problem",
              defaultMessage: "Problem",
            })}
            label={intl.formatMessage({
              id: "problem",
              defaultMessage: "Problem",
            })}
            fullWidth
          />
        </Grid>
        <Grid item sx={style.fieldSpacing}>
          <TextArea
            rows={8}
            value={fields.description}
            onChange={handleChange}
            name="description"
            placeholder={intl.formatMessage({
              id: "description",
              defaultMessage: "Description",
            })}
          />
        </Grid>
        <Grid item>
          {fileAttachments?.map((file: any, index: number) => (
            <Grid
              item
              sx={style.pdfContainer}
              key={`${file.fileName}-${index}`}
            >
              {file.isPdf ? (
                <PictureAsPdfIcon sx={{ color: "var(--orange-500)" }} />
              ) : (
                <ImageIcon sx={{ color: "var(--orange-500)" }} />
              )}
              <Typography component={"p"}>{file.fileName}</Typography>
              <Grid
                item
                sx={style.delete}
                onClick={() => removeAttachmentsBeforeSubmitTicket(index)}
              >
                <GetImages
                  name="DiagnosticTestRecordDeleteIcon"
                  width="12"
                  height="10"
                />
              </Grid>
            </Grid>
          ))}
        </Grid>
        <Grid
          item
          sx={{
            ...style.fieldSpacing,
            ...style.attachmentButton,
          }}
        >
          <input
            id="attachment"
            type="file"
            onChange={uploadFileToS3}
            accept={"image/png, image/jpeg, .pdf"}
            style={{ display: "none" }}
          />
          <Button
            isLoading={isFileUploading}
            variant={IButtonVariant.WHITE}
            btnTrigger={() => pdfImageUploadHandler("attachment")}
          >
            <GetImages name="GallerySmallIcon" width="26" height="20" />
            {intl.formatMessage({
              id: "attachment",
              defaultMessage: "Attachment",
            })}
          </Button>
        </Grid>
        <Grid item sx={{ ...style.helpSupportActions }}>
          <Grid item sx={{ ...style.cancelBtnSpace, ...style.button }}>
            <Button variant={IButtonVariant.WHITE} btnTrigger={closeHandler}>
              {intl.formatMessage({
                id: "cancel",
                defaultMessage: "Cancel",
              })}
            </Button>
          </Grid>
          <Grid item sx={style.button}>
            <Button
              disabled={
                !(
                  fields?.problem?.length > 0 && fields?.description?.length > 0
                )
              }
              btnTrigger={handleRequestCallbackSubmit}
            >
              {intl.formatMessage({
                id: "request_call_back",
                defaultMessage: "Request Call Back",
              })}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    ),
    [fileAttachments, fields, isFileUploading]
  );

  return (
    <>
      {isMobile || isTablet ? (
        <Modal isOpen={isOpen} closeHandler={closeHandler}>
          {helpSupportScreen()}
        </Modal>
      ) : (
        <Dialog
          fullWidth
          maxWidth={"sm"}
          isOpen={isOpen}
          closeHandler={closeHandler}
        >
          {helpSupportScreen()}
        </Dialog>
      )}
    </>
  );
};

export default HelpSupport;
